import React, { memo } from 'react';
import { FadeIn } from './ui/FadeIn';
import { Calendar, Repeat, Target, Users, Award, Trophy, Check } from 'lucide-react';
export function MentorshipProcessSection() {
  return <section id="proceso" className="pt-8 pb-24 md:pt-10 md:pb-32 bg-neutral-950 border-b border-neutral-900" data-id="element-78">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-79">
        {/* Header */}
        <FadeIn data-id="element-80">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-81">
            <div className="inline-flex flex-wrap items-center justify-center gap-2 bg-red-600/10 border border-red-600/20 px-3 sm:px-4 py-2 rounded-sm mb-8 max-w-full" data-id="element-82">
              <span className="text-[10px] sm:text-xs font-bold tracking-wide sm:tracking-widest text-red-500 uppercase text-center leading-relaxed" data-id="element-83">
                3 meses • 12 encuentros en vivo • Jueves: correcciones •
                Sábados: contenido / Q&A
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-white tracking-tight uppercase mb-6 break-words" data-id="element-84">
              ¿Cómo es el proceso de la Mentoría?
            </h2>
            <p className="text-lg text-neutral-400 leading-relaxed" data-id="element-85">
              La mentoría está diseñada como un proceso progresivo de
              aprendizaje, donde cada etapa construye sobre la anterior. No se
              trata de memorizar una estrategia. Se trata de desarrollar
              criterio para leer el mercado con claridad.
            </p>
          </div>
        </FadeIn>

        {/* 3 Stages Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16" data-id="element-86">
          {/* Card 1 */}
          <FadeIn delay={0.1} data-id="element-87">
            <div className="bg-[#121212] border border-neutral-800 p-8 rounded-sm h-full flex flex-col relative overflow-hidden group hover:border-red-900/50 transition-colors" data-id="element-88">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600/50 to-transparent opacity-50" data-id="element-89" />
              <div className="text-red-500 font-bold tracking-widest text-xs uppercase mb-3" data-id="element-90">
                (Primer mes)
              </div>
              <h3 className="text-xl font-black text-white uppercase tracking-wide mb-6" data-id="element-91">
                Etapa 1 — Mentalidad y Base del Trader
              </h3>

              <div className="text-neutral-400 text-sm leading-relaxed space-y-6 flex-1" data-id="element-92">
                <p data-id="element-93">
                  Antes de hablar de entradas o patrones, trabajamos algo mucho
                  más importante: la mentalidad con la que vas a enfrentar el
                  proceso. En esta etapa veremos:
                </p>
                <ul className="space-y-3" data-id="element-94">
                  {['cómo funciona realmente el aprendizaje en trading', 'por qué la mayoría abandona antes de entender el mercado', 'cómo gestionar la ansiedad y las expectativas', 'cómo sostener la práctica durante el proceso'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-95">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" data-id="element-96" />
                      <span className="text-neutral-300" data-id="element-97">{item}</span>
                    </li>)}
                </ul>

                <div className="pt-4 border-t border-neutral-800/50" data-id="element-98">
                  <p className="mb-2" data-id="element-99">
                    También se entrega la clase completa de:{' '}
                    <strong className="text-white font-bold" data-id="element-100">
                      Lenguaje de Precio
                    </strong>
                  </p>
                  <p className="mb-4" data-id="element-101">
                    Para comenzar a comprender cómo se mueve realmente el
                    mercado. Durante este mes aprenderás a:
                  </p>
                  <ul className="space-y-3" data-id="element-102">
                    {['identificar tendencias', 'reconocer impulsos del precio', 'detectar zonas relevantes', 'entender acumulaciones y rupturas'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-103">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" data-id="element-104" />
                        <span className="text-neutral-300" data-id="element-105">{item}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Card 2 */}
          <FadeIn delay={0.2} data-id="element-106">
            <div className="bg-[#121212] border border-neutral-800 p-8 rounded-sm h-full flex flex-col relative overflow-hidden group hover:border-red-900/50 transition-colors" data-id="element-107">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600/50 to-transparent opacity-50" data-id="element-108" />
              <div className="text-red-500 font-bold tracking-widest text-xs uppercase mb-3" data-id="element-109">
                (Segundo mes)
              </div>
              <h3 className="text-xl font-black text-white uppercase tracking-wide mb-6" data-id="element-110">
                Etapa 2 — El Protocolo Sniper
              </h3>

              <div className="text-neutral-400 text-sm leading-relaxed space-y-6 flex-1" data-id="element-111">
                <p data-id="element-112">
                  Una vez que el alumno aprende a leer el precio, se introduce
                  el sistema operativo del programa. Aquí aprenderás:
                </p>
                <ul className="space-y-3" data-id="element-113">
                  {['la estructura completa del Protocolo Sniper', 'el patrón 5-7', 'las confirmaciones necesarias', 'cómo validar escenarios', 'cuándo NO entrar al mercado'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-114">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" data-id="element-115" />
                      <span className="text-neutral-300" data-id="element-116">{item}</span>
                    </li>)}
                </ul>

                <div className="pt-4 mt-auto" data-id="element-117">
                  <p className="text-neutral-300 font-medium" data-id="element-118">
                    El objetivo es que puedas identificar los momentos donde el
                    mercado pierde eficiencia y aparecen las oportunidades
                    reales.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Card 3 */}
          <FadeIn delay={0.3} data-id="element-119">
            <div className="bg-[#121212] border border-neutral-800 p-8 rounded-sm h-full flex flex-col relative overflow-hidden group hover:border-red-900/50 transition-colors" data-id="element-120">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600/50 to-transparent opacity-50" data-id="element-121" />
              <div className="text-red-500 font-bold tracking-widest text-xs uppercase mb-3" data-id="element-122">
                (Tercer mes)
              </div>
              <h3 className="text-xl font-black text-white uppercase tracking-wide mb-6" data-id="element-123">
                Etapa 3 — Ejecución y Criterio Propio
              </h3>

              <div className="text-neutral-400 text-sm leading-relaxed space-y-6 flex-1" data-id="element-124">
                <p data-id="element-125">
                  En esta etapa el objetivo es consolidar lo aprendido y
                  transformar el sistema en un criterio personal. Se trabajará
                  sobre:
                </p>
                <ul className="space-y-3" data-id="element-126">
                  {['errores comunes del trader', 'sobreoperar y ansiedad', 'validación real de escenarios', 'construcción del criterio propio'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-127">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" data-id="element-128" />
                      <span className="text-neutral-300" data-id="element-129">{item}</span>
                    </li>)}
                </ul>

                <div className="pt-4 border-t border-neutral-800/50 mt-auto" data-id="element-130">
                  <p className="mb-2" data-id="element-131">
                    También se incluye una clase especial:{' '}
                    <strong className="text-white font-bold" data-id="element-132">
                      Introducción al Mercado de Futuros
                    </strong>
                  </p>
                  <p data-id="element-133">
                    Donde se explican las diferencias entre OTC y los mercados
                    profesionales.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* 6 Blocks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-134">
          {/* Block 1: Encuentros */}
          <FadeIn delay={0.4} data-id="element-135">
            <div className="bg-neutral-900/50 border border-neutral-800 p-6 rounded-sm h-full" data-id="element-136">
              <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center rounded-sm mb-4" data-id="element-137">
                <Calendar className="text-red-500 w-5 h-5" data-id="element-138" />
              </div>
              <h4 className="text-white font-bold uppercase tracking-wide mb-4" data-id="element-139">
                ¿Cuántos encuentros incluye?
              </h4>
              <div className="text-neutral-400 text-sm leading-relaxed space-y-3" data-id="element-140">
                <p data-id="element-141">Durante los 3 meses tendrás:</p>
                <ul className="space-y-2" data-id="element-142">
                  <li className="flex items-start gap-2" data-id="element-143">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" data-id="element-144" />
                    <span data-id="element-145">3 clases principales de contenido</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-146">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" data-id="element-147" />
                    <span data-id="element-148">6 sesiones de corrección en vivo</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-149">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" data-id="element-150" />
                    <span data-id="element-151">3 encuentros de preguntas y respuestas</span>
                  </li>
                </ul>
                <p className="text-white font-medium pt-2" data-id="element-152">
                  Total: 12 encuentros en vivo
                </p>
                <p data-id="element-153">
                  Además de la práctica guiada semanal y la comunidad privada.
                </p>
              </div>
            </div>
          </FadeIn>

          {/* Block 2: Dinámica */}
          <FadeIn delay={0.45} data-id="element-154">
            <div className="bg-neutral-900/50 border border-neutral-800 p-6 rounded-sm h-full" data-id="element-155">
              <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center rounded-sm mb-4" data-id="element-156">
                <Repeat className="text-red-500 w-5 h-5" data-id="element-157" />
              </div>
              <h4 className="text-white font-bold uppercase tracking-wide mb-4" data-id="element-158">
                Dinámica de la mentoría
              </h4>
              <div className="text-neutral-400 text-sm leading-relaxed space-y-4" data-id="element-159">
                <p data-id="element-160">
                  <strong className="text-white" data-id="element-161">Sábados:</strong> clases de
                  contenido o sesiones de preguntas y respuestas.
                </p>
                <p data-id="element-162">
                  <strong className="text-white" data-id="element-163">Jueves:</strong> correcciones
                  en vivo sobre los ejercicios de los alumnos.
                </p>
                <p className="text-red-400 font-medium bg-red-500/5 p-3 rounded-sm border border-red-500/10" data-id="element-164">
                  Esto permite: Aprender → Practicar → Corregir → Mejorar.
                </p>
              </div>
            </div>
          </FadeIn>

          {/* Block 3: Práctica */}
          <FadeIn delay={0.5} data-id="element-165">
            <div className="bg-neutral-900/50 border border-neutral-800 p-6 rounded-sm h-full" data-id="element-166">
              <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center rounded-sm mb-4" data-id="element-167">
                <Target className="text-red-500 w-5 h-5" data-id="element-168" />
              </div>
              <h4 className="text-white font-bold uppercase tracking-wide mb-4" data-id="element-169">
                Práctica real durante todo el programa
              </h4>
              <div className="text-neutral-400 text-sm leading-relaxed space-y-3" data-id="element-170">
                <p data-id="element-171">
                  Cada semana los alumnos deberán realizar ejercicios prácticos
                  como:
                </p>
                <ul className="space-y-2" data-id="element-172">
                  {['identificar estructuras de mercado', 'encontrar zonas relevantes', 'detectar escenarios del protocolo', 'validar confirmaciones'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-173">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" data-id="element-174" />
                      <span className="text-neutral-300" data-id="element-175">{item}</span>
                    </li>)}
                </ul>
                <p className="pt-2" data-id="element-176">
                  Los gráficos serán revisados durante las sesiones de
                  corrección.
                </p>
              </div>
            </div>
          </FadeIn>

          {/* Block 4: Comunidad */}
          <FadeIn delay={0.55} data-id="element-177">
            <div className="bg-neutral-900/50 border border-neutral-800 p-6 rounded-sm h-full" data-id="element-178">
              <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center rounded-sm mb-4" data-id="element-179">
                <Users className="text-red-500 w-5 h-5" data-id="element-180" />
              </div>
              <h4 className="text-white font-bold uppercase tracking-wide mb-4" data-id="element-181">
                Comunidad Privada
              </h4>
              <div className="text-neutral-400 text-sm leading-relaxed space-y-3" data-id="element-182">
                <p data-id="element-183">
                  Durante toda la mentoría tendrás acceso a un espacio donde
                  podrás:
                </p>
                <ul className="space-y-2" data-id="element-184">
                  {['enviar gráficos', 'realizar consultas', 'compartir análisis', 'aprender de los escenarios de otros alumnos'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-185">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 shrink-0" data-id="element-186" />
                      <span className="text-neutral-300" data-id="element-187">{item}</span>
                    </li>)}
                </ul>
                <p className="pt-2" data-id="element-188">
                  El objetivo es crear un entorno donde el aprendizaje sea
                  constante.
                </p>
              </div>
            </div>
          </FadeIn>

          {/* Block 5: Diferencial */}
          <FadeIn delay={0.6} data-id="element-189">
            <div className="bg-neutral-900/50 border border-neutral-800 p-6 rounded-sm h-full" data-id="element-190">
              <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center rounded-sm mb-4" data-id="element-191">
                <Award className="text-red-500 w-5 h-5" data-id="element-192" />
              </div>
              <h4 className="text-white font-bold uppercase tracking-wide mb-4" data-id="element-193">
                Diferencial del programa
              </h4>
              <div className="text-neutral-400 text-sm leading-relaxed space-y-4" data-id="element-194">
                <p data-id="element-195">
                  Esta mentoría no busca que dependas del mentor. El objetivo es
                  que desarrolles criterio propio.
                </p>
                <div className="pt-2 border-t border-neutral-800/50" data-id="element-196">
                  <p className="mb-2" data-id="element-197">
                    Durante el proceso también puede liberarse el uso del:{' '}
                    <strong className="text-white font-bold" data-id="element-198">
                      Asistente Visual Sniper
                    </strong>
                  </p>
                  <p data-id="element-199">
                    Una herramienta que ayuda a detectar escenarios, pero que
                    solo se utiliza cuando el alumno ya comprende el sistema
                    manual.
                  </p>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Block 6: Resultado */}
          <FadeIn delay={0.65} data-id="element-200">
            <div className="bg-neutral-900/50 border border-neutral-800 p-6 rounded-sm h-full" data-id="element-201">
              <div className="w-10 h-10 bg-red-500/10 flex items-center justify-center rounded-sm mb-4" data-id="element-202">
                <Trophy className="text-red-500 w-5 h-5" data-id="element-203" />
              </div>
              <h4 className="text-white font-bold uppercase tracking-wide mb-4" data-id="element-204">
                Resultado Final
              </h4>
              <div className="text-neutral-400 text-sm leading-relaxed space-y-3" data-id="element-205">
                <p data-id="element-206">Al finalizar los tres meses vas a tener:</p>
                <ul className="space-y-2" data-id="element-207">
                  {['comprensión real del movimiento del precio', 'dominio del Protocolo Sniper', 'criterio para identificar escenarios', 'mentalidad correcta para el trading', 'base para evolucionar hacia mercados profesionales'].map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-208">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" data-id="element-209" />
                      <span className="text-neutral-300" data-id="element-210">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}